import type { FieldProblem } from '../../lib/errorEnvelope.js';

/**
 * Pure helpers for `ModuleAnswersStep.tsx`'s save path and its per-field
 * validation-error display, split into their own non-JSX module so
 * `node --test` (this workspace's client test runner — see
 * `apps/client/package.json`'s `test` script, `--experimental-strip-types`
 * only, no JSX transform) can import and exercise them directly instead of
 * only regex-matching source text the way most other `apps/client` unit
 * tests do.
 */

/** Backend validation paths are the field key itself (`warehouseModel`) or,
 * for a `string-list`/`enumArray` item, that key plus an index suffix
 * (`warehouseNames[1]`) — strip the suffix to attribute the problem back to
 * its field row. */
export function baseFieldKeyOf(path: string): string {
  return path.replace(/\[\d+\]$/, '');
}

/** Groups a flat `details` array from a `VALIDATION_FAILED` envelope by the
 * field it belongs to, so a step can look up `problems.get(field.key)`. */
export function groupFieldProblems(
  details: readonly FieldProblem[] | undefined,
): Map<string, FieldProblem[]> {
  const byKey = new Map<string, FieldProblem[]>();
  for (const problem of details ?? []) {
    const key = baseFieldKeyOf(problem.path);
    const existing = byKey.get(key);
    if (existing) existing.push(problem);
    else byKey.set(key, [problem]);
  }
  return byKey;
}

/** Trims a string value/array items and drops empty ones before the value
 * is sent to the backend, which rejects a whitespace-only entry outright
 * (`apps/api/src/setup/module-answers.ts`'s `stringArray`/`string`
 * validators) rather than silently ignoring it — the "add row" button
 * leaves an empty string in the draft until the user types into it, so an
 * unsubmitted trailing row (or one left blank by mistake) used to fail the
 * whole step's save with one 400 that named the row's index but not which
 * on-screen field it belonged to. Returns `undefined` for "nothing left to
 * save", same as clearing the field. */
export function normalizeForSave(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (Array.isArray(value)) {
    const items = value
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item !== '');
    return items.length === 0 ? undefined : items;
  }
  return value;
}
